import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { ACCOUNT_TYPE, ACCOUNT_TYPE_LABELS } from '~/consts/accountTypes';

import { useUpdateAccount } from '~/hooks/apis/users/useUpdateAccount';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { AccountType } from '~/services/UsersService';

import { getAccountTypeOptions, isAccountTypeChangeable } from '~/utils/accountType';
import { canChangeAccountType } from '~/utils/adminPermission';

import { getErrorMessage } from './utils';

type Props = {
  accountId: string;
  accountType?: AccountType;
  onAccountTypeUpdated: (accountType: AccountType) => void;
};

// Explains what the account can and can't do after switching to the given type.
const ACCOUNT_TYPE_CHANGE_IMPACT: Partial<Record<AccountType, string>> = {
  [ACCOUNT_TYPE.SERVICE]:
    'will block it from signing in to portals. It will only be able to authenticate with client credentials or identity federation.',
  [ACCOUNT_TYPE.USER]:
    'will allow it to sign in to portals. It will no longer be able to authenticate with client credentials or identity federation.',
};

function getHelperText(hasPermission: boolean, accountType?: AccountType) {
  if (!hasPermission) {
    return 'Requires both the admin-user and role-admin roles.';
  }

  if (!accountType) {
    return 'The account type is unknown.';
  }

  if (accountType === ACCOUNT_TYPE.GUEST) {
    return 'Guest accounts cannot be changed.';
  }

  return undefined;
}

export default function AccountTypeField({ accountId, accountType, onAccountTypeUpdated }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useCurrentUser();
  const [pendingAccountType, setPendingAccountType] = useState<AccountType | null>(null);
  const updateAccountMutation = useUpdateAccount(accountId);

  const hasPermission = canChangeAccountType(currentUser);
  const isDisabled = !hasPermission || !isAccountTypeChangeable(accountType) || updateAccountMutation.isPending;
  const options = getAccountTypeOptions(accountType);

  const handleConfirm = async () => {
    if (!pendingAccountType) {
      return;
    }

    try {
      const updatedAccount = await updateAccountMutation.mutateAsync({ account_type: pendingAccountType });
      onAccountTypeUpdated(updatedAccount?.account_type ?? pendingAccountType);
      setPendingAccountType(null);
      enqueueSnackbar('Account type updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to update account type: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  return (
    <Box>
      <TextField
        select
        label="Account Type"
        value={accountType ?? ''}
        onChange={(event) => {
          const nextAccountType = event.target.value as AccountType;
          if (nextAccountType !== accountType) {
            setPendingAccountType(nextAccountType);
          }
        }}
        disabled={isDisabled}
        helperText={getHelperText(hasPermission, accountType)}
        sx={{ minWidth: 240 }}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {ACCOUNT_TYPE_LABELS[option]}
          </MenuItem>
        ))}
      </TextField>

      <Dialog
        open={Boolean(pendingAccountType)}
        onClose={updateAccountMutation.isPending ? undefined : () => setPendingAccountType(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Change Account Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Changing this account from <strong>{accountType && ACCOUNT_TYPE_LABELS[accountType]}</strong> to{' '}
            <strong>{pendingAccountType && ACCOUNT_TYPE_LABELS[pendingAccountType]}</strong>{' '}
            {pendingAccountType && ACCOUNT_TYPE_CHANGE_IMPACT[pendingAccountType]}
          </Typography>
          <Typography variant="body2">Do you want to continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAccountType(null)} disabled={updateAccountMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={updateAccountMutation.isPending}>
            Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
